import { DISPOSAL_REASONS } from '@felix/domain';
import { z } from 'zod';
import type {
  ProvisionCountInput,
  ProvisionDisposalInput,
  ProvisionIssueInput,
  ProvisionItem,
  ProvisionLevelsPatch,
  ProvisionLot,
  ProvisionReceiptInput,
} from './types.js';
import { fefoDeviation } from './view.js';

/**
 * Kumanya formları (React Hook Form + Zod; CLAUDE.md "Form ve doğrulama"). Kurallar gemi
 * sunucusunda `@felix/domain` ile yeniden denetlenir (ADR 0099 §6–§10); burada kullanıcıya erken
 * söylenir. Hata iletileri çeviri anahtarıdır (`provisions.formErrors.*`).
 */

const E = (code: string) => `provisions.formErrors.${code}`;
const MAX_QTY = 1_000_000_000;
const quantity = z.union([z.number(), z.literal('')]);
const text = (max: number) => z.string().max(max, E('tooLong'));

function positive(value: number | '', ctx: z.RefinementCtx, path: string) {
  if (value === '' || !(value > 0)) {
    ctx.addIssue({ code: 'custom', path: [path], message: E('positive') });
  } else if (value > MAX_QTY) {
    ctx.addIssue({ code: 'custom', path: [path], message: E('tooLarge') });
  }
}

const blank = (value: string) => {
  const trimmed = value.trim().replace(/\s+/g, ' ');
  return trimmed ? trimmed : null;
};

// ---------------------------------------------------------------------------------------------
// Stock In
// ---------------------------------------------------------------------------------------------

export interface ReceiptFormValues {
  materialId: string;
  quantity: number | '';
  batchNo: string;
  expiryDate: string;
  receivedDate: string;
  location: string;
  reason: string;
}

export function emptyReceiptForm(today: string, materialId = ''): ReceiptFormValues {
  return {
    materialId,
    quantity: '',
    batchNo: '',
    expiryDate: '',
    receivedDate: today,
    location: '',
    reason: '',
  };
}

/**
 * Kabul (ADR 0099 §6): `expiryTracking = Yes` son kullanma, `batchTracking = Yes` batch ister;
 * süresi geçmiş mal kabul edilmez; kabul günü gelecek olamaz.
 */
export function receiptSchema(
  today: string,
  item: Pick<ProvisionItem, 'batchTracking' | 'expiryTracking'> | null,
) {
  return z
    .object({
      materialId: z.string().min(1, E('required')),
      quantity,
      batchNo: text(100),
      expiryDate: z.string(),
      receivedDate: z.string().min(1, E('required')),
      location: text(200),
      reason: text(500),
    })
    .superRefine((values, ctx) => {
      positive(values.quantity, ctx, 'quantity');
      if (values.receivedDate > today) {
        ctx.addIssue({ code: 'custom', path: ['receivedDate'], message: E('future') });
      }
      if (item?.expiryTracking === 'Yes' && !values.expiryDate) {
        ctx.addIssue({ code: 'custom', path: ['expiryDate'], message: E('expiryRequired') });
      }
      if (item?.batchTracking === 'Yes' && !values.batchNo.trim()) {
        ctx.addIssue({ code: 'custom', path: ['batchNo'], message: E('batchRequired') });
      }
      if (values.expiryDate && values.expiryDate < values.receivedDate) {
        ctx.addIssue({ code: 'custom', path: ['expiryDate'], message: E('expired') });
      }
    });
}

export function toReceiptInput(values: ReceiptFormValues, today: string): ProvisionReceiptInput {
  return {
    materialId: values.materialId,
    quantity: Number(values.quantity),
    batchNo: blank(values.batchNo),
    expiryDate: values.expiryDate || null,
    receivedDate: values.receivedDate || today,
    location: blank(values.location),
    reason: blank(values.reason),
  };
}

// ---------------------------------------------------------------------------------------------
// Stock Out
// ---------------------------------------------------------------------------------------------

export interface IssueFormValues {
  stockItemId: string;
  quantity: number | '';
  /** `''`: otomatik FEFO. */
  lotId: string;
  reason: string;
}

export function emptyIssueForm(stockItemId = ''): IssueFormValues {
  return { stockItemId, quantity: '', lotId: '', reason: '' };
}

/**
 * Tüketim (ADR 0099 §7): FEFO zorunlu kalemde daha erken son kullanmalı lot varken başka lot
 * seçmek gerekçe ister; seçilen lotun bakiyesi yetmeli.
 */
export function issueSchema(
  today: string,
  item: Pick<ProvisionItem, 'fefoRequired'> | null,
  lots: readonly ProvisionLot[],
) {
  return z
    .object({
      stockItemId: z.string().min(1, E('required')),
      quantity,
      lotId: z.string(),
      reason: text(500),
    })
    .superRefine((values, ctx) => {
      positive(values.quantity, ctx, 'quantity');
      if (!values.lotId) return;
      const lot = lots.find((l) => l.id === values.lotId);
      if (lot && values.quantity !== '' && values.quantity > lot.balance) {
        ctx.addIssue({ code: 'custom', path: ['quantity'], message: E('lotBalance') });
      }
      if (
        item?.fefoRequired === true &&
        fefoDeviation(lots, values.lotId, today) &&
        !values.reason.trim()
      ) {
        ctx.addIssue({ code: 'custom', path: ['reason'], message: E('fefoReason') });
      }
    });
}

export function toIssueInput(values: IssueFormValues): ProvisionIssueInput {
  return {
    stockItemId: values.stockItemId,
    quantity: Number(values.quantity),
    lotId: values.lotId || null,
    reason: blank(values.reason),
  };
}

// ---------------------------------------------------------------------------------------------
// İmha / zayi
// ---------------------------------------------------------------------------------------------

export interface DisposalFormValues {
  lotId: string;
  quantity: number | '';
  reasonCode: string;
  reason: string;
}

export function emptyDisposalForm(lotId = ''): DisposalFormValues {
  return { lotId, quantity: '', reasonCode: '', reason: '' };
}

/** İmha (ADR 0099 §8): neden kodu ve açıklama zorunlu; lot izlemeli kalemde lot. */
export function disposalSchema(lotRequired: boolean, lots: readonly ProvisionLot[]) {
  return z
    .object({
      lotId: z.string(),
      quantity,
      reasonCode: z.string(),
      reason: text(500),
    })
    .superRefine((values, ctx) => {
      positive(values.quantity, ctx, 'quantity');
      if (lotRequired && !values.lotId) {
        ctx.addIssue({ code: 'custom', path: ['lotId'], message: E('lotRequired') });
      }
      const lot = lots.find((l) => l.id === values.lotId);
      if (lot && values.quantity !== '' && values.quantity > lot.balance) {
        ctx.addIssue({ code: 'custom', path: ['quantity'], message: E('lotBalance') });
      }
      if (!(DISPOSAL_REASONS as readonly string[]).includes(values.reasonCode)) {
        ctx.addIssue({ code: 'custom', path: ['reasonCode'], message: E('required') });
      }
      if (!values.reason.trim()) {
        ctx.addIssue({ code: 'custom', path: ['reason'], message: E('reasonRequired') });
      }
    });
}

export function toDisposalInput(
  stockItemId: string,
  values: DisposalFormValues,
): ProvisionDisposalInput {
  return {
    stockItemId,
    lotId: values.lotId || null,
    quantity: Number(values.quantity),
    reasonCode: values.reasonCode,
    reason: values.reason.trim(),
  };
}

// ---------------------------------------------------------------------------------------------
// Sayım
// ---------------------------------------------------------------------------------------------

export interface CountFormValues {
  /** Lot başına sayılan miktar; anahtar lot kimliği, `''` lotsuz bakiye. */
  counts: { lotId: string; counted: number | '' }[];
  reason: string;
}

/** Sayım (ADR 0099 §10): her satır sayılır (0 dahil), gerekçe zorunlu. */
export const countSchema = z
  .object({
    counts: z.array(z.object({ lotId: z.string(), counted: quantity })).min(1),
    reason: text(500),
  })
  .superRefine((values, ctx) => {
    values.counts.forEach((row, index) => {
      if (row.counted === '' || row.counted < 0) {
        ctx.addIssue({ code: 'custom', path: ['counts', index, 'counted'], message: E('counted') });
      }
    });
    if (!values.reason.trim()) {
      ctx.addIssue({ code: 'custom', path: ['reason'], message: E('reasonRequired') });
    }
  });

export function toCountInput(stockItemId: string, values: CountFormValues): ProvisionCountInput {
  return {
    stockItemId,
    counts: values.counts.map((row) => ({
      lotId: row.lotId || null,
      counted: Number(row.counted),
    })),
    reason: values.reason.trim(),
  };
}

// ---------------------------------------------------------------------------------------------
// Seviye ve yer
// ---------------------------------------------------------------------------------------------

export interface LevelsFormValues {
  location: string;
  minQty: number | '';
  reorderLevel: number | '';
  maxQty: number | '';
}

export function levelsFormOf(item: ProvisionItem): LevelsFormValues {
  return {
    location: item.location ?? '',
    minQty: item.minQty ?? 0,
    reorderLevel: item.reorderLevel ?? '',
    maxQty: item.maxQty ?? '',
  };
}

export const levelsSchema = z
  .object({
    location: text(200),
    minQty: quantity,
    reorderLevel: quantity,
    maxQty: quantity,
  })
  .superRefine((values, ctx) => {
    if (values.minQty === '' || values.minQty < 0) {
      ctx.addIssue({ code: 'custom', path: ['minQty'], message: E('notNegative') });
    }
    if (values.reorderLevel !== '' && values.reorderLevel < 0) {
      ctx.addIssue({ code: 'custom', path: ['reorderLevel'], message: E('notNegative') });
    }
    if (values.maxQty !== '' && values.minQty !== '' && values.maxQty < values.minQty) {
      ctx.addIssue({ code: 'custom', path: ['maxQty'], message: E('maxBelowMin') });
    }
  });

export function toLevelsPatch(rowVersion: number, values: LevelsFormValues): ProvisionLevelsPatch {
  return {
    rowVersion,
    location: blank(values.location),
    minQty: Number(values.minQty || 0),
    reorderLevel: values.reorderLevel === '' ? null : Number(values.reorderLevel),
    maxQty: values.maxQty === '' ? null : Number(values.maxQty),
  };
}
